import React, { useMemo } from "react";
import { useI18n } from "react-simple-i18n";
import { useParams } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { Box, Grid, Typography } from "@mui/material";
import { useQuery } from '@apollo/client';

import { lectureTypes } from "../config/config";
import { Layout } from "../components/layout/layout";
import { CourseCard, CourseCardSkeleton } from "./components/courseCard";
import { GlobalFirstSection, GlobalSpacing } from "../components/layout/styles";
import { GET_BROWSE_COURSE } from "../components/graphql";
import { PageNotFound } from "./404";

const emptyArray = new Array(4).fill(0);

const BrowseCourse = () => {
	const { t } = useI18n();
	const params = useParams(); // Get the 'id' from the URL

	const getActiveType = (paramID: string = "all"): any => {
		const selected = lectureTypes.find((type: SelectitemData) => (
			type.value === paramID
		))
		if (selected) return selected;
		else return { value: "all", label: "all" }
	}

	const lectureType: SelectitemData = getActiveType(params.id);
	const { data: browseCourse, loading, error } = useQuery(GET_BROWSE_COURSE, {
		variables: { lectureType: lectureType.value },
		pollInterval: 500,
	})

	const courseDatas: CourseObject[] = useMemo(() => {
		if (!!loading || !!error) return [];
		return browseCourse.getBrowseCourse;
	}, [browseCourse, loading, error])

	if (!!error) {
		return (<PageNotFound />)
	}

	return (
		<Layout>
			<GlobalSpacing>
				<BrowseCourseWrapper>
					<Typography variant="h3">
						{t(`header.${lectureType.value}`)} {t("common.courses")}
					</Typography>

					<Typography variant="body2">
						{t("common.course_description")}
					</Typography>

					{!loading && (
						<React.Fragment>
							<CoursesWrapper container spacing={2}>
								{courseDatas.map((data: CourseObject, key: number) => (
									<Grid item xs={12} md={6} lg={4} xl={3} key={key}>
										<CourseCard data={data} type={getActiveType(data.lectureType)?.value} />
									</Grid>
								))}
							</CoursesWrapper>

							{!courseDatas.length && (
								<Box className="no-courses">
									<Typography variant="h5">
										No courses available right now. Please check back later!
									</Typography>
								</Box>
							)}
						</React.Fragment>
					)}

					{!!loading && (
						<CoursesWrapper container spacing={2}>
							{emptyArray.map((i, key: number) => (
								<Grid item xs={12} md={6} lg={4} xl={3} key={key}>
									<CourseCardSkeleton />
								</Grid>
							))}
						</CoursesWrapper>
					)}
				</BrowseCourseWrapper>
			</GlobalSpacing>
		</Layout>
	)
}

const BrowseCourseWrapper = styled(GlobalFirstSection)(({ theme }) => ({
	display: 'flex',
	flexDirection: "column",
	gap: theme.spacing(2),
	padding: `${theme.spacing(5)} 0px`,

	h3: {
		fontWeight: 600,
	},

	p: {
		fontWeight: 500,
		opacity: 0.75,
	},


	".no-courses": {
		flex: 1,
		textAlign: "center",
		paddingTop: theme.spacing(3),

		h5: {
			color: `${theme.palette.common.firstColor}80`,
		}
	},

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
		padding: `${theme.spacing(3)} 0px`,
	}
}))

const CoursesWrapper = styled(Grid)(({ theme }) => ({
	marginTop: theme.spacing(0.5),

	[theme.breakpoints.down("md")]: {
		marginTop: theme.spacing(0),
	}
}))

export { BrowseCourse }